import { useSuspenseQuery } from "@apollo/client/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Plus, TriangleAlert } from "lucide-react";
import { Suspense, useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { extractExactI18nLabel } from "~/common/functions/i18nLabel";
import { useShowValidationErrors } from "~/common/hooks";
import type { PropertySchemaAssociationInput } from "~/common/types";
import { DetailLoading } from "~/components/loading";
import { Button, Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "~/components/ui";
import {
  GetCategoryForFormDocument,
  type GetCategoryForFormQuery,
  type GetCategoryForFormQueryVariables,
} from "~/features/admin/categories/gqls";
import { useCategoryList, useCategoryUpsert, useInheritedCategorySchemas } from "~/features/admin/categories/hooks";
import {
  type CategoryFormData,
  type CategoryRaw,
  zCategoryFormData,
  zCategoryFormDataKeys,
} from "~/features/admin/categories/types";
import {
  buildCategoryPath,
  CATEGORY_PATH_ROOT,
  isValidCategoryPath,
  splitCategoryPath,
  toPathSegment,
} from "~/features/admin/categories/utils/categoryPath";
import {
  hasRequiredPropertySchemas,
  PropertySchemaAssociationsList,
  usePropertySchemaAssociationsForm,
} from "~/features/admin/propertySchemas";
import { CategoryFormFields } from "./CategoryFormFields";
import { CategorySchemaMigrationSupportScope } from "./CategorySchemaMigrationSupportScope";
import { describeSchema, InheritedPropertySchemasSection } from "./InheritedPropertySchemasSection";

interface CategoryFormDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** If set, the dialog is in edit mode and prefills from this category id. */
  categoryId?: string;
}

const FORM_ID = "category-form";

const emptyDefaults: CategoryFormData = {
  id: undefined,
  typeId: "entity",
  name: "",
  // Almost every category hangs directly under the tree root, so start there.
  parentPath: CATEGORY_PATH_ROOT,
  pathSegment: "",
  description: "",
  jaLabel: "",
  enLabel: "",
  documentUrl: "",
  relationTypeId: null,
  propertySchemaAssociations: [],
};

export function CategoryFormDialog({ open, onOpenChange, categoryId }: CategoryFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = Boolean(categoryId);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="capitalize">
            {isEdit
              ? `${t("ui.edit")} ${t("model.category.modelName")}`
              : `${t("ui.add")} ${t("model.category.modelName")}`}
          </DialogTitle>
        </DialogHeader>
        <Suspense fallback={<DetailLoading />}>
          {isEdit && categoryId ? (
            <EditBody categoryId={categoryId} onClose={() => onOpenChange(false)} />
          ) : (
            <CreateBody onClose={() => onOpenChange(false)} />
          )}
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}

function CreateBody({ onClose }: { onClose: () => void }) {
  const { data } = useCategoryList();
  return (
    <FormBody defaults={emptyDefaults} categories={data.categories} prefilledAssociations={null} onClose={onClose} />
  );
}

function EditBody({ categoryId, onClose }: { categoryId: string; onClose: () => void }) {
  const { data } = useSuspenseQuery<GetCategoryForFormQuery, GetCategoryForFormQueryVariables>(
    GetCategoryForFormDocument,
    { variables: { categoryId } },
  );
  const { data: list } = useCategoryList();
  const category = data?.categories?.[0] as GetCategoryForFormQuery["categories"][number] | undefined;
  if (!category) return <div className="py-6 text-center text-14-regular text-fg-3">—</div>;
  // A category without a path (legacy data) is offered a slot under the root, like a new one.
  const { parentPath, segment } = category.categoryPath?.path
    ? splitCategoryPath(category.categoryPath.path)
    : { parentPath: CATEGORY_PATH_ROOT, segment: toPathSegment(category.name) };
  const defaults: CategoryFormData = {
    id: category.id,
    typeId: category.typeId,
    name: category.name,
    parentPath,
    pathSegment: segment,
    description: category.description ?? "",
    jaLabel: extractExactI18nLabel(category.i18nLabel as Record<string, unknown> | null, "ja") ?? "",
    enLabel: extractExactI18nLabel(category.i18nLabel as Record<string, unknown> | null, "en") ?? "",
    documentUrl: category.documentUrl ?? "",
    relationTypeId: category.relationTypeId ?? null,
    propertySchemaAssociations: [],
  };
  const associations = (category.categoryPropertySchemaSet?.[0]?.propertySchemaSet?.propertySchemaAssociations ??
    []) as Parameters<ReturnType<typeof usePropertySchemaAssociationsForm<CategoryFormData>>["setDefaults"]>[0];
  return (
    <FormBody defaults={defaults} categories={list.categories} prefilledAssociations={associations} onClose={onClose} />
  );
}

interface FormBodyProps {
  defaults: CategoryFormData;
  /** All categories of the tenant: parent options + names for the inherited-schema preview. */
  categories: readonly CategoryRaw[];
  /** Existing schemas to prefill on mount. `null` when creating. */
  prefilledAssociations:
    | Parameters<ReturnType<typeof usePropertySchemaAssociationsForm<CategoryFormData>>["setDefaults"]>[0]
    | null;
  onClose: () => void;
}

function FormBody({ defaults, categories, prefilledAssociations, onClose }: FormBodyProps) {
  const { t } = useTranslation();
  const methods = useForm<CategoryFormData>({
    resolver: zodResolver(zCategoryFormData),
    defaultValues: defaults,
  });
  const showValidationErrors = useShowValidationErrors(methods.setError, zCategoryFormDataKeys);

  const schemas = usePropertySchemaAssociationsForm<CategoryFormData>(methods.control);
  const [typeIdValue, parentPathValue, ownAssociations] = useWatch({
    control: methods.control,
    name: ["typeId", "parentPath", "propertySchemaAssociations"],
  });
  const isEdit = Boolean(defaults.id);

  const {
    parentOptions,
    inheritedSourceId,
    inheritedReady,
    inheritedSchemas,
    ownSchemaNames,
    conflicts,
    onInheritedLoaded,
  } = useInheritedCategorySchemas({
    categories,
    // When editing, the category's own subtree can never be its parent.
    ownPath: isEdit ? buildCategoryPath(defaults.parentPath, defaults.pathSegment) : null,
    parentPath: parentPathValue ?? null,
    ownAssociations,
  });

  // Prefill schemas on mount when editing.
  useEffect(() => {
    if (prefilledAssociations) {
      schemas.setDefaults(prefilledAssociations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults.id]);

  // Entity-create: keep the mandatory schemas seeded, minus the ones the chosen parent already provides.
  // Depends on the joined key so a same-content reload does not reset the rows.
  const inheritedKey = inheritedSchemas.map((s) => `${s.propertyAttributeTypeId}:${s.propertyTypeId}`).join(",");
  useEffect(() => {
    if (isEdit) return;
    if (typeIdValue === "entity") {
      if (!inheritedReady) return;
      schemas.reconcileMandatorySchemas(methods.getValues("propertySchemaAssociations") ?? [], inheritedSchemas);
      return;
    }
    if (typeIdValue === "relation") {
      schemas.remove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeIdValue, inheritedKey, inheritedReady]);

  const { submit, processing } = useCategoryUpsert({
    // The form edits the path as parent + segment; a backend "categoryPath" error belongs on the segment field.
    onError: (err) => {
      const formErrors = [];
      const collectionErrors = [];
      for (const field of err.fields ?? []) {
        const mappedField = field.path === "categoryPath" ? { ...field, code: null, path: "pathSegment" } : field;
        const parsedPath = zCategoryFormDataKeys.safeParse(mappedField.path);
        if (parsedPath.success && parsedPath.data !== "propertySchemaAssociations") {
          formErrors.push(mappedField);
        } else {
          collectionErrors.push(field);
        }
      }
      showValidationErrors(formErrors);
      // Collection/nested paths have no individual control to attach an error to. Keep the backend detail visible.
      collectionErrors.forEach((field) => toast.error(field.message));
    },
    onSuccess: () => {
      methods.reset();
      onClose();
    },
  });

  const onSubmit = (data: CategoryFormData) => {
    const categoryPath = buildCategoryPath(data.parentPath, data.pathSegment);
    if (!isValidCategoryPath(categoryPath)) {
      methods.setError("pathSegment", {
        type: "manual",
        message: t("feature.categories.errors.invalidCategoryPath"),
      });
      return;
    }
    if (
      !isEdit &&
      data.typeId === "entity" &&
      !hasRequiredPropertySchemas(data.propertySchemaAssociations ?? [], inheritedSchemas)
    ) {
      methods.setError("root", {
        type: "manual",
        message: t("feature.categories.errors.requiredPropertySchemas"),
      });
      return;
    }
    const passThrough = (data.propertySchemaAssociations ?? []) as unknown as PropertySchemaAssociationInput[];
    submit({ ...data, categoryPath }, passThrough);
  };

  return (
    <FormProvider {...methods}>
      <div className="max-h-[65vh] overflow-y-auto pr-1">
        <CategoryFormFields
          form={methods}
          formId={FORM_ID}
          onSubmit={onSubmit}
          parentOptions={parentOptions}
          allowRootParent={isEdit && defaults.parentPath === null}
        />
        <div className="mt-6">
          <InheritedPropertySchemasSection
            sourceCategoryId={inheritedSourceId}
            categories={categories}
            ownSchemaNames={ownSchemaNames}
            conflicts={conflicts}
            onLoaded={onInheritedLoaded}
          />
        </div>
        <div className="mt-6 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-16-bold capitalize text-fg-1">{t("model.propertySchema.modelNames")}</h2>
              <p className="text-12-regular text-fg-3">{t("feature.categories.form.ownSchemas.description")}</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={schemas.append}>
              <Plus />
              <span>{t("feature.dataImportProjects.form.addPropertySchema")}</span>
            </Button>
          </div>
          {isEdit && <CategorySchemaMigrationSupportScope />}
          {conflicts.length > 0 && (
            <div
              role="alert"
              className="flex gap-2 rounded-[var(--radius-md)] border border-border-default bg-bg-subtle px-3 py-2 text-danger"
            >
              <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="space-y-1">
                <p className="text-12-medium">{t("feature.categories.form.conflicts.title")}</p>
                <ul className="list-disc pl-4 text-12-regular">
                  {conflicts.map((conflict) => (
                    <li key={conflict.name}>
                      {t("feature.categories.form.conflicts.item", {
                        name: conflict.name,
                        own: describeSchema(conflict.own.propertyTypeId, conflict.own.propertyAttributeTypeId),
                        inherited: describeSchema(
                          conflict.inherited.propertyTypeId,
                          conflict.inherited.propertyAttributeTypeId,
                        ),
                        category: conflict.inherited.sourceCategoryName,
                      })}
                    </li>
                  ))}
                </ul>
                <p className="text-12-regular">{t("feature.categories.form.conflicts.description")}</p>
              </div>
            </div>
          )}
          <PropertySchemaAssociationsList
            fields={schemas.fields}
            onRemove={schemas.remove}
            onMove={schemas.move}
            showPropertyAttributeTypeId
          />
        </div>
        {methods.formState.errors.root?.message && (
          <p className="mt-2 text-12-medium text-danger">{methods.formState.errors.root.message}</p>
        )}
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button type="button" variant="outline" disabled={processing}>
            {t("ui.cancel")}
          </Button>
        </DialogClose>
        <Button type="submit" form={FORM_ID} disabled={processing || !inheritedReady}>
          {processing && <Loader2 className="animate-spin" />}
          <span>{t("ui.save")}</span>
        </Button>
      </DialogFooter>
    </FormProvider>
  );
}
